import { useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook for hover intent - opens on hover with a delay for closing
 * @param isOpen - Current open state
 * @param setIsOpen - State setter for open state
 * @param delay - Delay in milliseconds before closing (default: 300ms)
 * @returns Object with handleMouseEnter and handleMouseLeave functions
 */
export function useHoverIntent(isOpen: boolean, setIsOpen: (isOpen: boolean) => void, delay: number = 300) {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleMouseEnter = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setIsOpen(true);
    }, [setIsOpen]);

    const handleMouseLeave = useCallback(() => {
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, delay);
    }, [setIsOpen, delay]);

    const clearHoverTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    return {
        handleMouseEnter,
        handleMouseLeave,
        clearHoverTimeout,
        isOpen,
    };
}
